/** Utilities for extracting field-level errors from API responses (normalises FastAPI and nested shapes) */

import type { ApiError } from './api-client';

// ---------------------------------------------------------------------------
// FastAPI 422 validation detail entry
// ---------------------------------------------------------------------------

interface FastApiValidationEntry {
  loc: (string | number)[];
  msg: string;
  type?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isApiError(err: unknown): err is ApiError {
  return err !== null && typeof err === 'object' && 'status' in err && 'data' in err;
}

function isValidationArray(detail: unknown): detail is FastApiValidationEntry[] {
  return (
    Array.isArray(detail) &&
    detail.length > 0 &&
    typeof detail[0] === 'object' &&
    detail[0] !== null &&
    'loc' in detail[0] &&
    'msg' in detail[0]
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Extracts field-level errors from an API error response.
 *
 * Returns a flat `Record<string, string>` where each key is a field name
 * (e.g. `"name"`, `"description"`, or `"detail"` when no field is
 * identifiable) and the value is the human-readable error message.
 *
 * Handles:
 *   - FastAPI 422 validation arrays  (`{ detail: [{ loc, msg }] }`)
 *   - Simple detail strings           (`{ detail: "..." }`)
 *   - Nested response shapes          (`{ response: { data: { detail } } }`)
 *   - Plain Error objects              (`error.message`)
 *
 * @example
 * ```ts
 * try {
 *   await apiClient.post(url, body);
 * } catch (err) {
 *   const fields = extractFieldErrors(err);
 *   // { name: "A study with name 'Foo' already exists" }
 * }
 * ```
 */
export function extractFieldErrors(error: unknown): Record<string, string> {
  const fields: Record<string, string> = {};

  if (!error || typeof error !== 'object') {
    return fields;
  }

  // Helper to safely append messages to a field key without clobbering
  const appendField = (key: string, msg: string) => {
    if (fields[key]) {
      if (!fields[key].includes(msg)) {
        fields[key] = `${fields[key]} ${msg}`;
      }
    } else {
      fields[key] = msg;
    }
  };

  // 1. ApiError from api-client (has .data with the parsed response body)
  if (isApiError(error) && error.data && typeof error.data === 'object') {
    const data = error.data as Record<string, unknown>;

    // FastAPI 422: { detail: [{ loc: ["body","name"], msg: "..." }] }
    if (isValidationArray(data.detail)) {
      for (const entry of data.detail) {
        // loc is typically ["body", "field_name"] — grab the last element
        const fieldName = entry.loc.length > 0 ? String(entry.loc[entry.loc.length - 1]) : 'detail';
        appendField(fieldName, entry.msg);
      }
      return fields;
    }

    // Simple detail string: { detail: "A study with name 'X' already exists" }
    if (typeof data.detail === 'string') {
      appendField('detail', data.detail);
      return fields;
    }

    // Simple message string
    if (typeof data.message === 'string') {
      appendField('detail', data.message);
      return fields;
    }
  }

  // 2. Nested response shape (e.g. Axios-style): { response: { data: { detail } } }
  const maybeNested = error as { response?: { data?: Record<string, unknown> } };
  if (maybeNested.response?.data && typeof maybeNested.response.data === 'object') {
    const nestedData = maybeNested.response.data;

    if (isValidationArray(nestedData.detail)) {
      for (const entry of nestedData.detail) {
        const fieldName = entry.loc.length > 0 ? String(entry.loc[entry.loc.length - 1]) : 'detail';
        appendField(fieldName, entry.msg);
      }
      return fields;
    }

    if (typeof nestedData.detail === 'string') {
      appendField('detail', nestedData.detail);
      return fields;
    }
  }

  // 3. Plain Error or unknown object with .message
  if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
    appendField('detail', (error as { message: string }).message);
  }

  return fields;
}
